import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Clock, Wrench, ShieldCheck, MapPin, Factory, Info, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
    ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
       : "bg-slate-50 text-slate-600 border border-slate-200"
  }`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—");

export default function PublicAssetStatus() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/assets/${assetId}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Asset not found"));
  }, [assetId]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 max-w-md p-8 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h1 className="font-heading text-xl font-bold text-slate-900 mt-4">Asset not found</h1>
          <p className="text-sm text-slate-500 mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">Loading...</div>;
  }

  const a = data.asset || {};
  const statusColour = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    under_maintenance: "bg-amber-50 text-amber-800 border-amber-200",
    retired: "bg-slate-100 text-slate-600 border-slate-200",
    disposed: "bg-slate-100 text-slate-600 border-slate-200",
  }[a.status] || "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-asset-status">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Brand strip */}
        <div className="rounded-2xl bg-[#7C1D3A] text-white p-5 shadow-sm">
          <div className="text-[10px] tracking-widest uppercase opacity-70">{data.company_name}</div>
          <div className="font-heading text-lg font-semibold mt-0.5">Asset Verification · Public View</div>
          <div className="text-[11px] opacity-70 mt-1">Scanned at {fmt(data.as_of)}</div>
        </div>

        {/* Asset card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#7C1D3A]">{a.category}</div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-1">{a.name || "—"}</h1>
              <div className="text-xs font-mono text-slate-500 mt-1">{a.asset_id}</div>
            </div>
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-semibold uppercase tracking-wider ${statusColour}`}>
              {a.status?.replace(/_/g, " ") || "unknown"}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm border-t border-slate-100 pt-4">
            {a.manufacturer && (
              <div className="flex items-center gap-2 text-slate-700">
                <Factory className="w-4 h-4 text-slate-400" /> {a.manufacturer}
                {a.model_number && <span className="text-slate-500">· {a.model_number}</span>}
              </div>
            )}
            {a.serial_number && (
              <div className="flex items-center gap-2 text-slate-700">
                <Info className="w-4 h-4 text-slate-400" />
                <span className="font-mono text-xs">S/N {a.serial_number}</span>
              </div>
            )}
            {a.location && (
              <div className="flex items-center gap-2 text-slate-700">
                <MapPin className="w-4 h-4 text-slate-400" /> {a.location}
              </div>
            )}
            {a.installation_date && (
              <div className="flex items-center gap-2 text-slate-700">
                <Clock className="w-4 h-4 text-slate-400" /> Installed {fmt(a.installation_date)}
              </div>
            )}
          </div>

          {/* Warranty + AMC pills */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <Pill ok={data.warranty_active} label={data.warranty_active ? `Warranty valid till ${fmt(a.warranty_expires)}` : "Warranty expired / not on file"} />
            <Pill ok={data.amc_active} label={data.amc_active ? `AMC active till ${fmt(a.amc_end_date)}` : "No active AMC"} />
          </div>

          {/* Maintenance snapshot */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
              <Wrench className="w-4 h-4 text-[#7C1D3A]" /> Maintenance snapshot
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500">Last service</div>
                <div className="text-slate-800 mt-0.5">
                  {data.latest_maintenance
                    ? `${data.latest_maintenance.type} · ${fmt(data.latest_maintenance.date)} · ${data.latest_maintenance.status?.replace(/_/g, " ")}`
                    : "No record"}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Next scheduled</div>
                <div className="text-slate-800 mt-0.5">
                  {data.next_scheduled?.due_date || data.next_scheduled?.next_due_date
                    ? fmt(data.next_scheduled.due_date || data.next_scheduled.next_due_date)
                    : (data.latest_maintenance?.next_due_date ? fmt(data.latest_maintenance.next_due_date) : "No pending task")}
                </div>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-slate-500 flex items-center gap-1 pt-2 border-t border-slate-100">
            <ShieldCheck className="w-3.5 h-3.5" />
            Financial data (cost / depreciation) is intentionally hidden from this public view.
          </div>

          {/* Feb 2026 — CTA so on-floor techs can scan → jump into breakdown flow.
              Auth-gated route: unauthenticated users get bounced to /login and
              back to the pre-filled breakdown form. */}
          <button
            type="button"
            onClick={() => navigate(`/breakdown-report?asset=${a.id}`)}
            data-testid="public-report-breakdown-btn"
            className="w-full flex items-center justify-center gap-2 mt-2 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm shadow-sm transition-colors"
          >
            <AlertTriangle className="w-4 h-4" /> Report a Breakdown on this Asset
          </button>
        </div>

        <div className="text-center text-[11px] text-slate-400">
          Powered by Asset IQ · {data.company_name}
        </div>
      </div>
    </div>
  );
}
